import React from 'react';
import PropTypes from 'prop-types';
import { formatUrl } from 'url-lib';
import includes from 'lodash/includes';
import { parseFollowSignalExperimentVariants } from '@eb/event-renderer';

import {
    GRID_TYPE,
    STANDARD_STYLE,
    IMAGE_STYLE_FIXED,
    TYPES_PROP_TYPE,
    STYLES_PROP_TYPE,
    IMAGE_STYLES_PROP_TYPE,
} from '@eb/eds-media-card-content';
import {
    EnhancedEventCard,
    EVENT_CARD_SHARE_OPTIONS_PROPTYPE,
} from '@eb/eds-event-card';
import { getLocationInfo } from '../../SearchApp/utils/venue';
import SearchEventCardDebugInfo from './SearchEventCardDebugInfo';
import ExpandDedupedEventCTA from './ExpandDedupedEventCTA';

import {
    CITY_BROWSE_SECTION,
    SEARCH_SECTION,
    EVENT_AFFILIATES_MAP,
    GA_VIEW_EVENT_ACTION,
    GA_EVENT_CARD_CATEGORY_MAP,
    GA_TERM_MAP,
    GA_CATEGORY_MAP,
    GA_SHARE_ATTEMPT_ACTION,
    GA_SHARE_ACTION,
    GA_EXTERNAL_VIEW_EVENT_ACTION,
    GA_CREATE_COLLECTION_VIEW_ACTION,
    GA_ADD_TO_COLLECTION_SHOW_ACTION,
    SHARE_TYPE_GA_MAP,
} from '../../SearchApp/constants';

import {
    EVENTBRITE,
    EVENT_PROPTYPES,
    LINK_FOLLOW_ALLOWLIST,
} from '../../constants';

const _createShareUrl = (url, section) =>
    formatUrl(url, {
        'utm-medium': 'discovery',
        'utm-campaign': 'social',
        'utm-content': 'attendeeshare',
        'utm-source': 'cp',
        'utm-term': GA_TERM_MAP[section],
    });

export class SearchEventCard extends React.PureComponent {
    static propTypes = {
        ...EVENT_PROPTYPES,
        city: PropTypes.string,
        containerSpacingClasses: PropTypes.string,
        excludeExtraContent: PropTypes.bool,
        edgeColor: PropTypes.string,
        eventCollections: PropTypes.arrayOf(PropTypes.string),
        hideStartDate: PropTypes.bool,
        imageStyle: IMAGE_STYLES_PROP_TYPE,
        isAuthenticated: PropTypes.bool,
        isOnlineEvent: PropTypes.bool,
        neighborhood: PropTypes.string,
        onAddOrRemoveEventToCollection: PropTypes.func,
        onCreateCollection: PropTypes.func,
        onSaveEvent: PropTypes.func.isRequired,
        position: PropTypes.number,
        section: PropTypes.oneOf([CITY_BROWSE_SECTION, SEARCH_SECTION]),
        shareOptions: EVENT_CARD_SHARE_OPTIONS_PROPTYPE.isRequired,
        style: STYLES_PROP_TYPE,
        trackClickInternal: PropTypes.func,
        trackGAEvent: PropTypes.func,
        type: TYPES_PROP_TYPE,
        userCollections: PropTypes.arrayOf(PropTypes.object),
    };

    static defaultProps = {
        style: STANDARD_STYLE,
        type: GRID_TYPE,
        imageStyle: IMAGE_STYLE_FIXED,
        // eslint-disable-next-line react/default-props-match-prop-types
        venueName: '',
        // eslint-disable-next-line react/default-props-match-prop-types
        venueAddress: '',
        section: CITY_BROWSE_SECTION,
    };

    /* Callback on the onClick of a share option within the callback to pass
    to ga */
    _handleShareOptionClick = (shareType) => {
        const { section, trackGAEvent } = this.props;

        if (typeof trackGAEvent === 'function') {
            trackGAEvent({
                category: GA_EVENT_CARD_CATEGORY_MAP[section],
                action: GA_SHARE_ACTION,
                label: SHARE_TYPE_GA_MAP[shareType],
            });
        }
    };

    _handleShareClick = () => {
        const { id: eventId, section, trackGAEvent } = this.props;

        if (typeof trackGAEvent === 'function') {
            trackGAEvent({
                category: GA_CATEGORY_MAP[section],
                action: GA_SHARE_ATTEMPT_ACTION,
                label: eventId,
            });
        }
    };

    _handleEventClick = () => {
        const {
            id,
            trackClick,
            ticketsBy,
            trackClickInternal,
            numFollowers,
        } = this.props;

        if (trackClick) {
            const actionType =
                ticketsBy === EVENTBRITE
                    ? GA_VIEW_EVENT_ACTION
                    : GA_EXTERNAL_VIEW_EVENT_ACTION;

            trackClick(actionType, numFollowers);
        }

        if (trackClickInternal) {
            trackClickInternal(id, ticketsBy);
        }
    };

    _handleShowCreateCollectionView = () => {
        const { trackGAEvent, section, userCollections } = this.props;

        if (typeof trackGAEvent === 'function') {
            trackGAEvent({
                category: GA_CATEGORY_MAP[section],
                action: GA_CREATE_COLLECTION_VIEW_ACTION,
                label: userCollections ? `${userCollections.length}` : 0,
            });
        }
    };

    _handleShowAddToCollectionView = () => {
        const { trackGAEvent, section, userCollections } = this.props;

        if (typeof trackGAEvent === 'function') {
            trackGAEvent({
                category: GA_CATEGORY_MAP[section],
                action: GA_ADD_TO_COLLECTION_SHOW_ACTION,
                label: userCollections ? `${userCollections.length}` : 0,
            });
        }
    };

    render() {
        let {
            containerSpacingClasses,
            debugInfo,
            dedup,
            edgeColor,
            eventCollections,
            eventSalesStatus,
            excludeExtraContent,
            experiments,
            focused,
            hideStartDate,
            history,
            id,
            imageStyle,
            imageUrl,
            isAuthenticated,
            isFree,
            isOnlineEvent,
            isSoldOut,
            minPrice,
            name,
            onAddOrRemoveEventToCollection,
            onCreateCollection,
            onSaveEvent,
            position,
            repeatingInstanceCount,
            savedByYou,
            section,
            seriesUrl,
            shareOptions,
            shouldShowDebugOutput,
            startDate,
            startTime,
            style,
            ticketsBy,
            timezone,
            type,
            url,
            userCollections,
            venueAddress,
            venueName,
            numFollowers,
            followCountVariant,
        } = this.props;

        const eventUrl = _createShareUrl(url, section);

        const updatedUTMOptions = {
            ...shareOptions,
            eventUrl,
            eventName: name,
            eventId: id,
            onClick: this._handleShareOptionClick.bind(this),
            utmOptions: {
                'utm-term': GA_TERM_MAP[section],
            },
            trackingCode: EVENT_AFFILIATES_MAP[section],
        };

        const locationInfo = getLocationInfo(venueName, venueAddress);

        let priceInfo;

        /* We want to default the cards on search pages to not include the
        extra content unless explicitly passed through */
        if (section === SEARCH_SECTION) {
            // On search event cards we want price information
            priceInfo = minPrice;
            excludeExtraContent = true;
        }

        // Since we currently noindex children events,
        // we link their event cards to the parent event page (which is indexed)
        // so that parent events can be crawled.
        // See https://jira.evbhome.com/browse/EB-109015
        if (seriesUrl) {
            url = seriesUrl;
        }

        const shouldShowExpandDedupedEventCTA =
            dedup && typeof dedup.count === 'number' && dedup.count > 1;

        const signal = parseFollowSignalExperimentVariants(
            numFollowers,
            followCountVariant,
        );

        return (
            <div
                className="search-event-card eds-l-mar-vert-1"
                ref={this.myRef}
            >
                <EnhancedEventCard
                    containerSpacingClasses={containerSpacingClasses}
                    edgeColor={edgeColor}
                    eventCollections={eventCollections}
                    eventSalesStatus={eventSalesStatus}
                    excludeDateThumbnail={!!excludeExtraContent}
                    experiments={experiments}
                    focused={focused}
                    id={id}
                    imageStyle={imageStyle}
                    imageUrl={imageUrl}
                    isAuthenticated={isAuthenticated}
                    isConsumer={true}
                    isFree={isFree}
                    isOnlineEvent={isOnlineEvent}
                    isLazyImage={true}
                    isNoFollow={!includes(LINK_FOLLOW_ALLOWLIST, ticketsBy)}
                    isSoldOut={isSoldOut}
                    isTargetBlank={true}
                    locationInfo={locationInfo}
                    minPrice={priceInfo}
                    name={name}
                    onAddOrRemoveEventToCollection={
                        onAddOrRemoveEventToCollection
                    }
                    onClick={this._handleEventClick}
                    onCreateCollection={onCreateCollection}
                    onDidShareClick={this._handleShareOptionClick}
                    onSaveEvent={onSaveEvent}
                    onShowAddToCollectionView={
                        this._handleShowAddToCollectionView
                    }
                    onShowCreateCollectionView={
                        this._handleShowCreateCollectionView
                    }
                    onWillShareClick={this._handleShareClick}
                    repeatingInstanceCount={repeatingInstanceCount}
                    savedByYou={savedByYou}
                    shareOptions={updatedUTMOptions}
                    shouldConvertToUserTimezone={true}
                    startDate={hideStartDate ? null : startDate}
                    startTime={hideStartDate ? null : startTime}
                    style={style}
                    ticketsBy={ticketsBy}
                    timezone={hideStartDate ? null : timezone}
                    type={type}
                    url={url}
                    userCollections={userCollections}
                    {...signal}
                />
                {shouldShowExpandDedupedEventCTA && (
                    <ExpandDedupedEventCTA
                        dedup={dedup}
                        history={history}
                        name={name}
                        eventId={id}
                    />
                )}
                {debugInfo &&
                    debugInfo.esDebugInfo &&
                    shouldShowDebugOutput && (
                        <SearchEventCardDebugInfo
                            debugInfo={debugInfo}
                            position={position}
                            numFollowers={numFollowers}
                        />
                    )}
            </div>
        );
    }
}
export default SearchEventCard;
